import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import { LOGOS } from '../constants/assets';

const Landing = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const fadeInUp = {
    initial: { opacity: 0, y: 20 },
    animate: { opacity: 1, y: 0 },
    transition: { duration: 0.6 }
  };

  const services = [
    { title: "Talent Sourcing", desc: "Finding the perfect match for your organization through rigorous screening.", icon: "Search", color: "bg-blue-50 text-[#11469A]" },
    { title: "Workforce Management", desc: "Comprehensive solutions for managing your most valuable assets—your people.", icon: "Users", color: "bg-cyan-50 text-[#00AEEF]" },
    { title: "Executive Search", desc: "Discreet and professional placement of high-level leadership talent.", icon: "Briefcase", color: "bg-purple-50 text-purple-600" },
    { title: "Payroll Services", desc: "Streamlined payroll and compliance management for your entire staff.", icon: "CreditCard", color: "bg-green-50 text-green-600" }
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Navigation */}
      <nav className="fixed top-0 w-full z-[100] bg-white sm:bg-white/90 sm:backdrop-blur-md border-b border-gray-100 h-16 sm:h-20 flex items-center px-4 sm:px-12">
        <div className="max-w-7xl mx-auto w-full flex justify-between items-center">
          <img src={LOGOS.dark} alt="RM Ghana" className="h-8 sm:h-10 w-auto object-contain" />
          
          {/* Desktop Menu */}
          <div className="hidden md:flex items-center gap-8 font-semibold text-sm text-gray-600">
            <a href="#about" className="hover:text-[#11469A] transition-colors">About</a>
            <a href="#services" className="hover:text-[#11469A] transition-colors">Services</a>
            <a href="#jobs" className="hover:text-[#11469A] transition-colors">Jobs</a>
            <Link to="/login" className="text-[#11469A] hover:opacity-80 transition-opacity">Sign In</Link>
            <Link to="/signup" className="bg-[#11469A] text-white px-6 py-2.5 rounded-full hover:bg-[#00AEEF] transition-all shadow-md">
              Register
            </Link>
          </div>

          {/* Mobile Menu Toggle */}
          <button 
            className="md:hidden p-2 text-gray-600 hover:text-[#11469A] transition-colors"
            onClick={() => setIsMenuOpen(!isMenuOpen)}
            aria-label="Toggle Menu"
          >
            <SafeIcon name={isMenuOpen ? "X" : "Menu"} size={24} />
          </button>
        </div>
      </nav>

      {/* Mobile Menu Overlay */}
      <AnimatePresence>
        {isMenuOpen && (
          <>
            {/* Backdrop */}
            <motion.div 
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setIsMenuOpen(false)}
              className="fixed inset-0 bg-black/40 backdrop-blur-sm z-[80] md:hidden"
            />
            
            {/* Menu Content */}
            <motion.div 
              initial={{ x: '100%' }}
              animate={{ x: 0 }}
              exit={{ x: '100%' }}
              transition={{ type: 'spring', damping: 25, stiffness: 200 }}
              className="fixed right-0 top-0 bottom-0 w-[80%] max-w-sm bg-white z-[90] p-8 md:hidden flex flex-col pt-24 shadow-2xl"
            >
              <div className="flex flex-col gap-8">
                <a href="#about" onClick={() => setIsMenuOpen(false)} className="text-xl font-bold text-gray-800 flex items-center gap-3">
                  <SafeIcon name="Info" size={20} className="text-[#00AEEF]" />
                  About RM Ghana
                </a>
                <a href="#services" onClick={() => setIsMenuOpen(false)} className="text-xl font-bold text-gray-800 flex items-center gap-3">
                  <SafeIcon name="Grid" size={20} className="text-[#00AEEF]" />
                  Our Services
                </a>
                <a href="#jobs" onClick={() => setIsMenuOpen(false)} className="text-xl font-bold text-gray-800 flex items-center gap-3">
                  <SafeIcon name="Briefcase" size={20} className="text-[#00AEEF]" />
                  Job Board
                </a>
                
                <div className="h-px bg-gray-100 my-4"></div>
                
                <Link to="/login" onClick={() => setIsMenuOpen(false)} className="text-xl font-bold text-[#11469A] flex items-center gap-3">
                  <SafeIcon name="LogIn" size={20} />
                  Sign In
                </Link>
                <Link 
                  to="/signup" 
                  onClick={() => setIsMenuOpen(false)} 
                  className="bg-[#11469A] text-white px-6 py-4 rounded-xl font-bold text-center shadow-lg hover:bg-[#00AEEF] transition-all flex items-center justify-center gap-2"
                >
                  Register Now
                  <SafeIcon name="ArrowRight" size={18} />
                </Link>
              </div>
              
              <div className="mt-auto pt-10 text-center">
                <p className="text-xs text-gray-400 font-medium">© 2026 RM Ghana Portal</p>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>

      {/* Hero Section */}
      <section className="pt-24 sm:pt-32 pb-16 sm:pb-24 px-4 sm:px-12 overflow-hidden bg-white">
        <div className="max-w-7xl mx-auto grid lg:grid-cols-2 gap-12 items-center">
          <motion.div {...fadeInUp} className="text-center lg:text-left">
            <span className="inline-block px-4 py-1.5 rounded-full bg-blue-50 text-[#11469A] text-[10px] sm:text-xs font-bold uppercase tracking-widest mb-6">
              Leading Workforce Solutions in Ghana
            </span>
            <h1 className="text-3xl sm:text-5xl lg:text-6xl font-extrabold text-[#11469A] leading-tight mb-6">
              Build Your Career with <span className="text-[#00AEEF]">RMGhana.</span>
            </h1>
            <p className="text-base sm:text-lg text-gray-500 mb-8 sm:10 max-w-lg mx-auto lg:mx-0 leading-relaxed">
              Join a dynamic team making impact across Ghana. Explore our open positions and find the role that fits your expertise and ambitions.
            </p>
            <div className="flex flex-col sm:flex-row flex-wrap gap-4 justify-center lg:justify-start">
              <Link to="/signup" className="px-8 py-4 bg-[#11469A] text-white rounded-xl font-bold hover:bg-[#0d3678] transition-all shadow-lg hover:shadow-xl transform hover:-translate-y-1 text-center">
                Get Started Now
              </Link>
              <Link to="/login" className="px-8 py-4 bg-white text-[#11469A] border-2 border-[#11469A] rounded-xl font-bold hover:bg-gray-50 transition-all text-center">
                Client Portal
              </Link>
            </div>
          </motion.div>
          <motion.div 
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.8 }}
            className="relative hidden sm:block lg:block"
          >
            <img 
              src="https://media-manager-c.questera.ai/greta-media/3140d8d6cb9117fc579edbc74f55e47991f06b3358b3a2dbff9e876f24bde08089c96c4a7bef32e6eede9d743c938dc3/images/aW1hZ2Uvd2VicA==/21854ae94410c3bd790cd7d379cfee38.webp" 
              className="rounded-3xl shadow-2xl relative z-10 w-full h-[350px] sm:h-[450px] lg:h-[500px] object-cover" 
              alt="RM Ghana Career" 
            />
          </motion.div>
        </div>
      </section>

      {/* Overlapping Search Section */}
      <div className="relative z-30 px-4 sm:px-12">
        <div className="max-w-3xl mx-auto -mb-10 sm:-mb-12">
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.3 }}
            className="bg-white rounded-[2rem] sm:rounded-full shadow-xl p-1.5 flex flex-col sm:flex-row border-[6px] border-gray-200 items-stretch"
          >
            <div className="flex-1 flex items-center px-4 sm:px-8 py-2 sm:py-0">
              <SafeIcon name="Search" className="text-gray-400 mr-3" size={20} />
              <input 
                type="text" 
                placeholder="Search by role, department or location" 
                className="w-full bg-transparent outline-none text-gray-700 placeholder-gray-500 font-medium py-2 sm:py-4 text-sm sm:text-base text-center sm:text-left" 
              />
            </div>
            <button className="bg-[#294B98] hover:bg-[#1a3670] text-white px-8 sm:px-12 py-3.5 sm:py-4 rounded-[1.5rem] sm:rounded-r-full sm:rounded-l-none font-bold transition-all whitespace-nowrap text-sm sm:text-base mt-1 sm:mt-0 shadow-sm">
              Search
            </button>
          </motion.div>
        </div>
      </div>

      {/* Services Section */}
      <section id="services" className="pt-20 sm:pt-24 pb-16 sm:pb-20 bg-gray-50 px-4 sm:px-12">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12 sm:mb-16">
            <h2 className="text-2xl sm:text-3xl font-bold text-[#11469A] mb-4">Our Expertise</h2>
            <p className="text-sm sm:text-base text-gray-500 max-w-2xl mx-auto px-4">
              We provide end-to-end human resource solutions tailored to the unique landscape of the West African market.
            </p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 sm:gap-8">
            {services.map((service, idx) => (
              <motion.div 
                key={idx}
                className="bg-white p-6 sm:p-8 rounded-2xl shadow-sm hover:shadow-xl transition-all duration-300 border border-gray-100 group text-left"
              >
                <div className={`w-12 h-12 sm:w-14 sm:h-14 rounded-xl ${service.color} flex items-center justify-center mb-6 group-hover:scale-110 transition-transform`}>
                  <SafeIcon name={service.icon} size={24} />
                </div>
                <h3 className="text-base sm:text-lg font-bold text-gray-800 mb-3">{service.title}</h3>
                <p className="text-xs sm:text-sm text-gray-500 leading-relaxed font-medium">{service.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-16 sm:py-20 bg-[#11469A] text-white px-4 sm:px-12 relative overflow-hidden">
        <div className="absolute inset-0 opacity-10" style={{ backgroundImage: 'radial-gradient(circle, white 1px, transparent 1px)', backgroundSize: '30px 30px' }}></div>
        <div className="max-w-7xl mx-auto grid grid-cols-2 lg:grid-cols-4 gap-8 sm:gap-12 text-center relative z-10">
          <div>
            <div className="text-3xl sm:text-4xl font-extrabold mb-2">12k+</div>
            <div className="text-blue-200 text-[10px] sm:text-sm font-bold uppercase tracking-widest">Candidates</div>
          </div>
          <div>
            <div className="text-3xl sm:text-4xl font-extrabold mb-2">500+</div>
            <div className="text-blue-200 text-[10px] sm:text-sm font-bold uppercase tracking-widest">Placements</div>
          </div>
          <div>
            <div className="text-3xl sm:text-4xl font-extrabold mb-2">98%</div>
            <div className="text-blue-200 text-[10px] sm:text-sm font-bold uppercase tracking-widest">Satisfaction</div>
          </div>
          <div>
            <div className="text-3xl sm:text-4xl font-extrabold mb-2">15+</div>
            <div className="text-blue-200 text-[10px] sm:text-sm font-bold uppercase tracking-widest">Industries</div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-10 sm:py-12 bg-gray-900 text-gray-400 px-4 sm:px-12">
        <div className="max-w-7xl mx-auto flex flex-col md:flex-row justify-between items-center gap-8">
          <img src={LOGOS.dark} alt="RM Ghana" className="h-6 sm:h-8 brightness-0 invert opacity-50" />
          <p className="text-xs sm:text-sm font-medium text-center">© 2026 Resources & Manpower Ghana. All rights reserved.</p>
          <div className="flex gap-6">
            <SafeIcon name="Linkedin" size={20} className="hover:text-white cursor-pointer transition-colors" />
            <SafeIcon name="Twitter" size={20} className="hover:text-white cursor-pointer transition-colors" />
            <SafeIcon name="Facebook" size={20} className="hover:text-white cursor-pointer transition-colors" />
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Landing;